import type { ListBuilderConfiguration } from '../types';

/**
 * Batch status value mapping
 */
const BATCH_STATUS_VALUE_MAPPING = [
	{
		value: 'pending',
		label: 'Pending',
		color: { bg: '#e5e7eb', text: '#4b5563' },
	},
	{
		value: 'scheduled',
		label: 'Scheduled',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
	{
		value: 'running',
		label: 'Running',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
	{
		value: 'completed',
		label: 'Completed',
		color: { bg: '#dcfce7', text: '#166534' },
	},
	{
		value: 'failed',
		label: 'Failed',
		color: { bg: '#fee2e2', text: '#991b1b' },
	},
	{
		value: 'cancelled',
		label: 'Cancelled',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
	{
		value: 'recurring',
		label: 'Recurring',
		color: { bg: '#e0e7ff', text: '#3730a3' },
	},
];

/**
 * Automation Batches list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_BATCHES_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-automation-batches',
			cacheTTL: 600000,
			name: 'All Campaigns',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'batch-name',
					headers: [{ id: 'batch-name-header', text: 'Campaign' }],
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								type: 'object',
								enabled: true,
							},
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'flow-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'flow.name',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							prefix: 'Flow: ',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_batch',
							fieldName: 'name',
							direction: 'ASC',
						},
					],
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'batchStatus',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: BATCH_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 120,
					sortFields: [
						{
							objectTable: '_automation_batch',
							fieldName: 'batchStatus',
							direction: 'ASC',
						},
					],
				},
				{
					id: 'progress',
					headers: [{ id: 'progress-header', text: 'Progress' }],
					fields: [
						{
							id: 'progress-field',
							dataConfig: {
								type: 'computed',
								computation: {
									type: 'percentage',
									numerator: 'completedCount',
									denominator: 'totalCount',
								},
							},
							styleConfig: {
								size: 'sm',
							},
							displayStyle: 'progress',
						},
						{
							id: 'line-break-progress',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'progress-text',
							dataConfig: {
								type: 'computed',
								computation: {
									type: 'template',
									template: '{completedCount} / {totalCount}',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					width: 200,
				},
				{
					id: 'created-date',
					headers: [{ id: 'created-date-header', text: 'Created' }],
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'createdDate',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'date',
							formatOptions: {
								dateStyle: 'short',
							},
						},
					],
					sortable: true,
					width: 120,
					sortFields: [
						{
							objectTable: '_automation_batch',
							fieldName: 'createdDate',
							direction: 'DESC',
						},
					],
				},
				{
					id: 'scheduled-date',
					headers: [{ id: 'scheduled-date-header', text: 'Scheduled' }],
					fields: [
						{
							id: 'scheduled-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'scheduledAt',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'date',
							formatOptions: {
								dateStyle: 'short',
								timeStyle: 'short',
							},
							emptyValue: '-',
						},
					],
					sortable: true,
					width: 150,
					sortFields: [
						{
							objectTable: '_automation_batch',
							fieldName: 'scheduledAt',
							direction: 'DESC',
						},
					],
				},
				{
					id: 'next-run',
					headers: [{ id: 'next-run-header', text: 'Next Run' }],
					fields: [
						{
							id: 'next-run-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'nextRunAt',
									objectTable: 'automation_batch',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'date',
							formatOptions: {
								dateStyle: 'short',
								timeStyle: 'short',
							},
							emptyValue: '-',
						},
					],
					sortable: true,
					width: 150,
					sortFields: [
						{
							objectTable: '_automation_batch',
							fieldName: 'nextRunAt',
							direction: 'ASC',
						},
					],
				},
			],
			defaultSortId: 'created-date-desc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'automationBatchKeywords',
			'automationBatchStatus',
			'automationBatchCreatedDate',
			'automationBatchParentId',
		],
		defaultFilters: {
			automationBatchParentId: { id: 'top-level', label: 'Top-level only' },
		},
	},
	objects: [
		{
			baseUrl: '/automation/campaigns/:id',
			objectLabel: 'Campaign',
			objectTable: '_automation_batch',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'batchStatus', fieldLabel: 'Status' },
				{ fieldName: 'totalCount', fieldLabel: 'Total Count' },
				{ fieldName: 'completedCount', fieldLabel: 'Completed Count' },
				{ fieldName: 'failedCount', fieldLabel: 'Failed Count' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'scheduledAt', fieldLabel: 'Scheduled At' },
				{ fieldName: 'startedAt', fieldLabel: 'Started At' },
				{ fieldName: 'completedAt', fieldLabel: 'Completed At' },
				{ fieldName: 'isRecurring', fieldLabel: 'Is Recurring' },
				{ fieldName: 'isActive', fieldLabel: 'Is Active' },
				{ fieldName: 'parentBatchId', fieldLabel: 'Parent Campaign ID' },
				{ fieldName: 'nextRunAt', fieldLabel: 'Next Run At' },
				{ fieldName: 'runCount', fieldLabel: 'Run Count' },
			],
		},
	],
};
